/**
 * Workflow Editor Component
 * Visual drag-and-drop workflow builder using React Flow
 */
import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Connection,
  Edge,
  Node,
  addEdge,
  useNodesState,
  useEdgesState,
  NodeTypes,
  Panel,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import styles from './WorkflowEditor.module.css';
import api from '../../../services/api';
import { 
  GitBranch,
  ToggleLeft,
  Repeat,
  Bot,
  Wrench,
  Code,
  Shuffle,
  StickyNote,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
  FileText,
  PlayCircle,
  Download,
  Copy,
  Check,
  Settings,
  Trash2,
} from 'lucide-react';

// Import custom nodes
import { StartNode } from './nodes/StartNode';
import { EndNode } from './nodes/EndNode';
import { AssistantNode } from './nodes/AssistantNode';
import { ConditionalNode } from './nodes/ConditionalNode';
import { SwitchNode } from './nodes/SwitchNode';
import { IteratorNode } from './nodes/IteratorNode';
import { ToolNode } from './nodes/ToolNode';
import { CustomNode } from './nodes/CustomNode';
import { TransformNode } from './nodes/TransformNode';
import { NoteNode } from './nodes/NoteNode';
import { NodeConfigPanel } from './NodeConfigPanel';
import { 
  workflowToYAML, 
  downloadWorkflowYAML, 
  copyWorkflowYAMLToClipboard,
  WorkflowGlobalConfig 
} from './utils/yamlConverter';

// Node categories for palette
const NODE_CATEGORIES = {
  control: [
    { type: 'conditional', label: 'Conditional', icon: GitBranch, color: '#f59e0b', description: 'Branch based on conditions' },
    { type: 'switch', label: 'Switch', icon: ToggleLeft, color: '#f59e0b', description: 'Multiple path selection' },
    { type: 'iterator', label: 'Iterator', icon: Repeat, color: '#f59e0b', description: 'Loop through items' },
  ],
  action: [
    { type: 'assistant', label: 'Assistant', icon: Bot, color: '#2563eb', description: 'AI assistant node' },
    { type: 'tool', label: 'Tool', icon: Wrench, color: '#2563eb', description: 'Execute a tool' },
    { type: 'custom', label: 'Custom', icon: Code, color: '#ec4899', description: 'Custom code execution' },
    { type: 'transform', label: 'Transform', icon: Shuffle, color: '#f97316', description: 'Transform data' },
  ],
  other: [
    { type: 'note', label: 'Note', icon: StickyNote, color: '#a855f7', description: 'Add documentation' },
  ],
};

// Flatten for lookup
const NODE_PALETTE = [
  ...NODE_CATEGORIES.control,
  ...NODE_CATEGORIES.action,
  ...NODE_CATEGORIES.other,
];

// Custom node types mapping
const nodeTypes: NodeTypes = {
  start: StartNode,
  end: EndNode,
  assistant: AssistantNode,
  conditional: ConditionalNode,
  switch: SwitchNode,
  iterator: IteratorNode,
  tool: ToolNode,
  custom: CustomNode,
  transform: TransformNode,
  note: NoteNode,
};

interface WorkflowEditorProps {
  workflowId?: string;
  workflowName: string;
  initialNodes?: Node[];
  initialEdges?: Edge[];
  onSave?: (nodes: Node[], edges: Edge[]) => void;
  onSaveAndRun?: (nodes: Node[], edges: Edge[]) => Promise<void>;
  onClose?: () => void;
}

export const WorkflowEditor: React.FC<WorkflowEditorProps> = ({
  workflowId,
  workflowName,
  initialNodes = [],
  initialEdges = [],
  onSave,
  onSaveAndRun,
  onClose,
}) => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    initialNodes.length > 0 ? initialNodes : [
      {
        id: 'start',
        type: 'start',
        position: { x: 100, y: 200 },
        data: { label: 'Start' },
      },
      {
        id: 'end',
        type: 'end',
        position: { x: 600, y: 200 },
        data: { label: 'End' },
      },
    ]
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);
  const [isPaletteCollapsed, setIsPaletteCollapsed] = useState(false);
  const [yamlCopied, setYamlCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  
  // Workflow global configuration state
  const [workflowConfig, setWorkflowConfig] = useState<WorkflowGlobalConfig>({
    enableSummarization: false,
    maxConcurrency: 1,
    recursionLimit: 300,
    messagesLimitBeforeSummarization: 30,
    tokensLimitBeforeSummarization: 3000000,
    retryPolicy: {
      backoffFactor: 5,
      initialIntervalSeconds: 5,
      maxAttempts: 9,
      maxIntervalSeconds: 50,
    },
  });
  const [isConfigCollapsed, setIsConfigCollapsed] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Assistants state for node configuration
  const [assistants, setAssistants] = useState<Array<{ id: string; name: string }>>([]);

  // Fetch assistants on mount
  useEffect(() => {
    const fetchAssistants = async () => {
      try {
        const response = await api.listAssistants();
        console.log('Assistants API response:', response);
        if (response.success && response.data) {
          const assistantsList = response.data.map((a: any) => ({
            id: a.id,
            name: a.name,
          }));
          console.log('Mapped assistants:', assistantsList);
          setAssistants(assistantsList);
        }
      } catch (error) {
        console.error('Failed to fetch assistants:', error);
      }
    };
    fetchAssistants();
  }, []);

  // Handle new connections
  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            animated: true,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
              color: '#2563eb',
            },
            style: {
              strokeWidth: 2,
              stroke: '#2563eb',
            },
          },
          eds
        )
      );
    },
    [setEdges]
  );

  // Handle drag over for dropping nodes
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Check if a position is inside an iterator node
  const findIteratorAtPosition = useCallback((x: number, y: number): Node | null => {
    const iterators = nodes.filter(n => n.type === 'iterator');
    for (const iterator of iterators) {
      const width = iterator.data?.width || 300;
      const height = iterator.data?.height || 200;
      const headerHeight = 36; // Header height to offset
      
      if (
        x >= iterator.position.x &&
        x <= iterator.position.x + width &&
        y >= iterator.position.y + headerHeight &&
        y <= iterator.position.y + height
      ) {
        return iterator;
      }
    }
    return null;
  }, [nodes]);

  // Check if a node has an incoming edge (has a parent connector)
  const hasIncomingEdge = useCallback((nodeId: string): boolean => {
    return edges.some(e => e.target === nodeId);
  }, [edges]);

  // Update iterator's child node list based on positions
  // IMPORTANT: Only nodes with incoming edges (parent connections) can be inside an iterator
  const updateIteratorChildren = useCallback(() => {
    setNodes((nds) => {
      const iterators = nds.filter(n => n.type === 'iterator');
      let updated = false;
      
      const newNodes = nds.map(n => {
        if (n.type !== 'iterator') return n;
        
        const width = n.data?.width || 300;
        const height = n.data?.height || 200;
        const headerHeight = 36;
        
        // Find all nodes inside this iterator that have an incoming edge
        const childNodeIds = nds
          .filter(child => {
            if (child.type === 'iterator' || child.type === 'start' || child.type === 'end') return false;
            if (child.id === n.id) return false;
            
            // MUST CONDITION: Node must have an incoming edge to be inside iterator
            const hasParentConnection = edges.some(e => e.target === child.id);
            if (!hasParentConnection) return false;
            
            const childCenterX = child.position.x + 90;
            const childCenterY = child.position.y + 33;
            
            return (
              childCenterX >= n.position.x &&
              childCenterX <= n.position.x + width &&
              childCenterY >= n.position.y + headerHeight &&
              childCenterY <= n.position.y + height
            );
          })
          .map(child => child.id);
        
        if (JSON.stringify(childNodeIds) !== JSON.stringify(n.data?.childNodeIds || [])) {
          updated = true;
          return {
            ...n,
            data: {
              ...n.data,
              childNodeIds,
            },
          };
        }
        return n;
      });
      
      // Also update nodes to know which iterator they belong to
      // Only assign parentIteratorId if the node has an incoming edge
      const finalNodes = newNodes.map(n => {
        if (n.type === 'iterator' || n.type === 'start' || n.type === 'end') return n;
        
        // MUST CONDITION: Node must have an incoming edge to belong to an iterator
        const hasParentConnection = edges.some(e => e.target === n.id);
        
        // If no incoming edge, cannot be inside any iterator
        if (!hasParentConnection) {
          if (n.data?.parentIteratorId) {
            return {
              ...n,
              data: {
                ...n.data,
                parentIteratorId: undefined,
              },
            };
          }
          return n;
        }
        
        // Find if this node is inside any iterator
        const parentIterator = iterators.find(it => {
          const width = it.data?.width || 300;
          const height = it.data?.height || 200;
          const headerHeight = 36;
          
          const childCenterX = n.position.x + 90;
          const childCenterY = n.position.y + 33;
          
          return (
            childCenterX >= it.position.x &&
            childCenterX <= it.position.x + width &&
            childCenterY >= it.position.y + headerHeight &&
            childCenterY <= it.position.y + height
          );
        });
        
        const newParentIteratorId = parentIterator?.id;
        if (n.data?.parentIteratorId !== newParentIteratorId) {
          return {
            ...n,
            data: {
              ...n.data,
              parentIteratorId: newParentIteratorId,
            },
          };
        }
        return n;
      });
      
      return finalNodes;
    });
  }, [setNodes, edges]);

  // Auto-resize iterator based on children positions
  const autoResizeIterators = useCallback(() => {
    setNodes((nds) => {
      const nonIteratorNodes = nds.filter(n => n.type !== 'iterator');
      
      return nds.map(n => {
        if (n.type !== 'iterator') return n;
        
        // Get child nodes for this iterator
        const childIds = n.data?.childNodeIds || [];
        if (childIds.length === 0) return n;
        
        const children = nonIteratorNodes.filter(c => childIds.includes(c.id));
        if (children.length === 0) return n;
        
        // Calculate bounding box of children
        const padding = 40; // Padding around children
        const headerHeight = 50; // Header height
        const nodeWidth = 180; // Approximate node width
        const nodeHeight = 66; // Approximate node height
        
        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;
        
        children.forEach(child => {
          minX = Math.min(minX, child.position.x);
          minY = Math.min(minY, child.position.y);
          maxX = Math.max(maxX, child.position.x + nodeWidth);
          maxY = Math.max(maxY, child.position.y + nodeHeight);
        });
        
        // Calculate new dimensions
        const requiredWidth = maxX - minX + padding * 2;
        const requiredHeight = maxY - minY + padding * 2 + headerHeight;
        
        const currentWidth = n.data?.width || 300;
        const currentHeight = n.data?.height || 200;
        
        // Only expand, don't shrink below current size or minimum
        const newWidth = Math.max(300, currentWidth, requiredWidth);
        const newHeight = Math.max(200, currentHeight, requiredHeight);
        
        // Check if iterator position needs adjustment (if children are outside left/top)
        const iterX = n.position.x;
        const iterY = n.position.y;
        
        const needsReposition = minX < iterX + padding || minY < iterY + headerHeight + padding;
        
        if (needsReposition || newWidth !== currentWidth || newHeight !== currentHeight) {
          return {
            ...n,
            position: {
              x: needsReposition ? Math.min(iterX, minX - padding) : iterX,
              y: needsReposition ? Math.min(iterY, minY - headerHeight - padding) : iterY,
            },
            data: {
              ...n.data,
              width: newWidth,
              height: newHeight,
            },
          };
        }
        
        return n;
      });
    });
  }, [setNodes]);

  // Handle drop to create new node
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type || !reactFlowWrapper.current) return;

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const dropX = event.clientX - reactFlowBounds.left;
      const dropY = event.clientY - reactFlowBounds.top;

      // Find if dropped inside an iterator
      const parentIterator = findIteratorAtPosition(dropX, dropY);
      
      // Position for the new node (always absolute, no parent-child relationship)
      const position = { x: dropX - 128, y: dropY - 32 };
      
      // Determine if node should be associated with an iterator
      let parentIteratorId: string | undefined;
      if (parentIterator && type !== 'iterator' && type !== 'start' && type !== 'end') {
        parentIteratorId = parentIterator.id;
      }

      // Set z-index: iterators should be behind other nodes
      const zIndex = type === 'iterator' ? 0 : 10;

      const newNode: Node = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        zIndex,
        // Don't use parentNode - it causes edge rendering issues
        data: { 
          label: type.charAt(0).toUpperCase() + type.slice(1),
          stateId: `${type}_${Date.now()}`,
          isConnected: false,
          parentIteratorId,
        },
      };

      setNodes((nds) => [...nds, newNode]);

      // Update iterator children after adding node and auto-resize
      setTimeout(() => {
        updateIteratorChildren();
        setTimeout(() => autoResizeIterators(), 50);
      }, 50);
    },
    [setNodes, findIteratorAtPosition, updateIteratorChildren, autoResizeIterators]
  );

  // Handle node drag stop - update iterator memberships
  const onNodeDragStop = useCallback((_: React.MouseEvent, node: Node) => {
    // Skip for iterators, start, and end nodes
    if (node.type === 'iterator' || node.type === 'start' || node.type === 'end') return;
    
    // Update iterator children after drag and auto-resize
    setTimeout(() => {
      updateIteratorChildren();
      setTimeout(() => autoResizeIterators(), 50);
    }, 50);
  }, [updateIteratorChildren, autoResizeIterators]);

  // Handle node selection
  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
    setSelectedEdge(null); // Clear edge selection when node is selected
    setIsConfigCollapsed(false);
  }, []);

  // Handle edge selection
  const onEdgeClick = useCallback((_: React.MouseEvent, edge: Edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null); // Clear node selection when edge is selected
    setIsConfigCollapsed(false);
  }, []);

  // Delete edge by ID
  const deleteEdge = useCallback((edgeId: string) => {
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    setSelectedEdge(null);
    setIsConfigCollapsed(true);
  }, [setEdges]);

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  // Handle node drag start from palette
  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  // Delete node by ID (also handles iterator children)
  const deleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => {
      const nodeToDelete = nds.find(n => n.id === nodeId);
      
      // If deleting an iterator, also remove child references
      if (nodeToDelete?.type === 'iterator') {
        // Find nodes inside this iterator and clear their parentIteratorId
        const childNodeIds = nodeToDelete.data?.childNodeIds || [];
        return nds
          .filter((n) => n.id !== nodeId)
          .map(n => {
            if (childNodeIds.includes(n.id)) {
              return {
                ...n,
                data: {
                  ...n.data,
                  parentIteratorId: undefined,
                },
              };
            }
            return n;
          });
      }
      
      // Regular node deletion
      return nds.filter((n) => n.id !== nodeId);
    });
    
    // Delete all edges connected to this node
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
    setIsConfigCollapsed(true);
    
    // Update iterator children after deletion and auto-resize
    setTimeout(() => {
      updateIteratorChildren();
      setTimeout(() => autoResizeIterators(), 50);
    }, 50);
  }, [setNodes, setEdges, updateIteratorChildren, autoResizeIterators]);

  // Duplicate node by ID
  const duplicateNode = useCallback((nodeId: string) => {
    const nodeToDuplicate = nodes.find(n => n.id === nodeId);
    if (nodeToDuplicate && nodeToDuplicate.type !== 'start' && nodeToDuplicate.type !== 'end') {
      const newNode: Node = {
        ...nodeToDuplicate,
        id: `${nodeToDuplicate.type}-${Date.now()}`,
        position: {
          x: nodeToDuplicate.position.x + 50,
          y: nodeToDuplicate.position.y + 50,
        },
        data: {
          ...nodeToDuplicate.data,
          stateId: `${nodeToDuplicate.type}_${Date.now()}`,
        },
      };
      setNodes((nds) => [...nds, newNode]);
    }
  }, [nodes, setNodes]);

  // Update node data
  const updateNodeData = useCallback((nodeId: string, newData: any) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId
          ? { ...n, data: newData }
          : n
      )
    );
    // Update selectedNode if it's the one being updated
    setSelectedNode((prev) => 
      prev?.id === nodeId ? { ...prev, data: newData } : prev
    );
  }, [setNodes]);

  // Delete selected node (kept for keyboard shortcut)
  const deleteSelectedNode = useCallback(() => {
    if (selectedNode && selectedNode.type !== 'start' && selectedNode.type !== 'end') {
      deleteNode(selectedNode.id);
    }
  }, [selectedNode, deleteNode]);

  // Duplicate selected node (kept for keyboard shortcut)
  const duplicateSelectedNode = useCallback(() => {
    if (selectedNode && selectedNode.type !== 'start' && selectedNode.type !== 'end') {
      duplicateNode(selectedNode.id);
    }
  }, [selectedNode, duplicateNode]);

  // Save workflow
  const handleSave = useCallback(async () => {
    if (onSave) {
      setIsSaving(true);
      setSaveStatus('saving');
      try {
        await onSave(nodes, edges);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (error) {
        console.error('Failed to save workflow:', error);
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 3000);
      } finally {
        setIsSaving(false);
      }
    }
  }, [nodes, edges, onSave]);

  // Save and Run workflow
  const handleSaveAndRun = useCallback(async () => {
    if (!onSaveAndRun) {
      console.warn('onSaveAndRun callback not provided');
      return;
    }
    
    setIsRunning(true);
    setSaveStatus('saving');
    try {
      await onSaveAndRun(nodes, edges);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to save and run workflow:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsRunning(false);
    }
  }, [nodes, edges, onSaveAndRun]);

  // Generate YAML from current workflow
  const generatedYAML = useMemo(() => {
    return workflowToYAML(
      nodes,
      edges,
      { name: workflowName, description: 'Workflow generated from visual editor' },
      workflowConfig
    );
  }, [nodes, edges, workflowName, workflowConfig]);

  // Download YAML file
  const handleDownloadYAML = useCallback(() => {
    downloadWorkflowYAML(
      nodes,
      edges,
      { name: workflowName, description: 'Workflow generated from visual editor' },
      workflowConfig
    );
  }, [nodes, edges, workflowName, workflowConfig]);

  // Copy YAML to clipboard
  const handleCopyYAML = useCallback(async () => {
    const success = await copyWorkflowYAMLToClipboard(
      nodes,
      edges,
      { name: workflowName, description: 'Workflow generated from visual editor' },
      workflowConfig
    );
    if (success) {
      setYamlCopied(true);
      setTimeout(() => setYamlCopied(false), 2000);
    }
  }, [nodes, edges, workflowName, workflowConfig]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  // Keyboard event handler for delete/backspace
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if the target is an input or textarea (don't delete when typing)
      const target = event.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Handle Delete or Backspace key
      if (event.key === 'Delete' || event.key === 'Backspace') {
        // Delete selected edge
        if (selectedEdge) {
          event.preventDefault();
          deleteEdge(selectedEdge.id);
          return;
        }
        
        // Delete selected node (except start/end)
        if (selectedNode && selectedNode.type !== 'start' && selectedNode.type !== 'end') {
          event.preventDefault();
          deleteNode(selectedNode.id);
        }
      }

      // Handle Ctrl/Cmd + D for duplicate
      if ((event.ctrlKey || event.metaKey) && event.key === 'd') {
        if (selectedNode && selectedNode.type !== 'start' && selectedNode.type !== 'end') {
          event.preventDefault();
          duplicateNode(selectedNode.id);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedNode, selectedEdge, deleteNode, deleteEdge, duplicateNode]);

  // Memoize node types to prevent re-renders
  const memoizedNodeTypes = useMemo(() => nodeTypes, []);

  // Active tab state
  const [activeTab, setActiveTab] = useState<'canvas' | 'yaml' | 'settings'>('canvas');

  return (
    <div className={`${styles.editorContainer} ${isFullscreen ? styles.fullscreen : ''}`}>
      {/* Header */}
      <div className={styles.editorHeader}>
        <div className={styles.headerLeft}>
          <button className={styles.backButton} onClick={onClose}>
            <X size={20} />
          </button>
          <div className={styles.workflowInfo}>
            <h1>{workflowName}</h1>
            <span className={styles.workflowId}>{workflowId || 'New Workflow'}</span>
          </div>
        </div>
        <div className={styles.headerCenter}>
          <div className={styles.tabs}>
            <button 
              className={`${styles.tab} ${activeTab === 'canvas' ? styles.active : ''}`}
              onClick={() => setActiveTab('canvas')}
            >
              Canvas
            </button>
            <button 
              className={`${styles.tab} ${activeTab === 'yaml' ? styles.active : ''}`}
              onClick={() => setActiveTab('yaml')}
            >
              <FileText size={14} />
              YAML
            </button>
            <button 
              className={`${styles.tab} ${activeTab === 'settings' ? styles.active : ''}`}
              onClick={() => setActiveTab('settings')}
            >
              Workflow Config
            </button>
          </div>
        </div>
        <div className={styles.headerRight}>
          {saveStatus === 'saved' && (
            <span className={styles.saveIndicator}>
              <Check size={14} />
              Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className={styles.errorIndicator}>
              Save Failed
            </span>
          )}
          <button 
            className={styles.runButton} 
            onClick={handleSaveAndRun}
            disabled={isRunning || isSaving}
          >
            {isRunning ? (
              <>
                <Settings size={16} className={styles.spinning} />
                Running...
              </>
            ) : (
              <>
                <PlayCircle size={16} />
                Save and Run
              </>
            )}
          </button>
          <button 
            className={styles.saveButton} 
            onClick={handleSave}
            disabled={isSaving || isRunning}
          >
            {isSaving ? (
              <>
                <Settings size={16} className={styles.spinning} />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save
              </>
            )}
          </button>
          <button className={styles.iconButton} onClick={toggleFullscreen}>
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
      </div>

      {/* Main Editor Area */}
      <div className={styles.editorMain}>
        {/* Node Palette - Only show on canvas tab */}
        {activeTab === 'canvas' && (
        <div className={`${styles.nodePalette} ${isPaletteCollapsed ? styles.collapsed : ''}`}>
          <div className={styles.paletteHeader}>
            <h3>Nodes</h3>
            <button 
              className={styles.collapseButton}
              onClick={() => setIsPaletteCollapsed(!isPaletteCollapsed)}
            >
              {isPaletteCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>
          {!isPaletteCollapsed && (
            <div className={styles.paletteContent}>
              <p className={styles.paletteHint}>Drag to canvas</p>
              
              {/* Control Section */}
              <div className={styles.paletteSection}>
                <h4>Control</h4>
                {NODE_CATEGORIES.control.map((node) => (
                  <div
                    key={node.type}
                    className={styles.paletteNode}
                    draggable
                    onDragStart={(e) => onDragStart(e, node.type)}
                  >
                    <div className={styles.nodeIcon} style={{ backgroundColor: node.color }}>
                      <node.icon size={18} color="white" />
                    </div>
                    <span className={styles.nodeLabel}>{node.label}</span>
                  </div>
                ))}
              </div>

              {/* Action Section */}
              <div className={styles.paletteSection}>
                <h4>Action</h4>
                {NODE_CATEGORIES.action.map((node) => (
                  <div
                    key={node.type}
                    className={styles.paletteNode}
                    draggable
                    onDragStart={(e) => onDragStart(e, node.type)}
                  >
                    <div className={styles.nodeIcon} style={{ backgroundColor: node.color }}>
                      <node.icon size={18} color="white" />
                    </div>
                    <span className={styles.nodeLabel}>{node.label}</span>
                  </div>
                ))}
              </div>

              {/* Other Section */}
              <div className={styles.paletteSection}>
                <h4>Other</h4>
                {NODE_CATEGORIES.other.map((node) => (
                  <div
                    key={node.type}
                    className={styles.paletteNode}
                    draggable
                    onDragStart={(e) => onDragStart(e, node.type)}
                  >
                    <div className={styles.nodeIcon} style={{ backgroundColor: node.color }}>
                      <node.icon size={18} color="white" />
                    </div>
                    <span className={styles.nodeLabel}>{node.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        )}

        {/* Canvas */}
        {activeTab === 'canvas' && (
        <div className={styles.canvasWrapper} ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges.map(e => ({
              ...e,
              // Highlight selected edge with different style
              style: {
                ...e.style,
                stroke: selectedEdge?.id === e.id ? '#ef4444' : (e.style?.stroke || '#2563eb'),
                strokeWidth: selectedEdge?.id === e.id ? 3 : (e.style?.strokeWidth || 2),
              },
            }))}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onNodeDragStop={onNodeDragStop}
            onPaneClick={onPaneClick}
            nodeTypes={memoizedNodeTypes}
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: true,
              style: {
                strokeWidth: 2,
                stroke: '#2563eb',
                strokeDasharray: '5,5',
              },
            }}
          >
            <Background color="#e2e8f0" gap={20} size={1} />
            <Controls />
            <MiniMap 
              nodeColor={(node) => {
                const palette = NODE_PALETTE.find(n => n.type === node.type);
                return palette?.color || '#2563eb';
              }}
            />
            <Panel position="bottom-center">
              <div className={styles.canvasInfo}>
                <span>{nodes.length} nodes</span>
                <span>•</span>
                <span>{edges.length} connections</span>
              </div>
            </Panel>
          </ReactFlow>
        </div>
        )}

        {/* YAML View */}
        {activeTab === 'yaml' && (
          <div className={styles.yamlViewContainer}>
            <div className={styles.yamlToolbar}>
              <h3>Workflow YAML</h3>
              <div className={styles.yamlActions}>
                <button 
                  className={styles.yamlButton} 
                  onClick={handleCopyYAML}
                  title="Copy to clipboard"
                >
                  {yamlCopied ? <Check size={16} /> : <Copy size={16} />}
                  {yamlCopied ? 'Copied!' : 'Copy'}
                </button>
                <button 
                  className={styles.yamlButton}
                  onClick={handleDownloadYAML}
                  title="Download YAML file"
                >
                  <Download size={16} />
                  Download
                </button>
              </div>
            </div>
            <div className={styles.yamlContent}>
              <pre className={styles.yamlCode}>{generatedYAML}</pre>
            </div>
          </div>
        )}

        {/* Settings View */}
        {activeTab === 'settings' && (
          <div className={styles.settingsViewContainer}>
            <div className={styles.settingsSection}>
              <h3>Workflow Configuration</h3>
              <p className={styles.settingsDescription}>
                Configure global workflow execution settings
              </p>
            </div>

            <div className={styles.settingsGrid}>
              {/* Performance Settings */}
              <div className={styles.settingsCard}>
                <h4>Performance</h4>
                <div className={styles.settingField}>
                  <label>Max Concurrency</label>
                  <input
                    type="number"
                    value={workflowConfig.maxConcurrency}
                    onChange={(e) => setWorkflowConfig(prev => ({
                      ...prev,
                      maxConcurrency: parseInt(e.target.value) || 1
                    }))}
                    min={1}
                    max={10}
                  />
                  <span className={styles.settingHint}>Maximum parallel executions</span>
                </div>
                <div className={styles.settingField}>
                  <label>Recursion Limit</label>
                  <input
                    type="number"
                    value={workflowConfig.recursionLimit}
                    onChange={(e) => setWorkflowConfig(prev => ({
                      ...prev,
                      recursionLimit: parseInt(e.target.value) || 300
                    }))}
                    min={1}
                    max={1000}
                  />
                  <span className={styles.settingHint}>Maximum recursion depth</span>
                </div>
              </div>

              {/* Summarization Settings */}
              <div className={styles.settingsCard}>
                <h4>Summarization</h4>
                <div className={styles.settingField}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={workflowConfig.enableSummarization}
                      onChange={(e) => setWorkflowConfig(prev => ({
                        ...prev,
                        enableSummarization: e.target.checked
                      }))}
                    />
                    Enable Summarization Node
                  </label>
                </div>
                <div className={styles.settingField}>
                  <label>Tokens Limit Before Summarization</label>
                  <input
                    type="number"
                    value={workflowConfig.tokensLimitBeforeSummarization}
                    onChange={(e) => setWorkflowConfig(prev => ({
                      ...prev,
                      tokensLimitBeforeSummarization: parseInt(e.target.value) || 3000000
                    }))}
                    min={1000}
                    step={10000}
                  />
                </div>
                <div className={styles.settingField}>
                  <label>Messages Limit Before Summarization</label>
                  <input
                    type="number"
                    value={workflowConfig.messagesLimitBeforeSummarization}
                    onChange={(e) => setWorkflowConfig(prev => ({
                      ...prev,
                      messagesLimitBeforeSummarization: parseInt(e.target.value) || 30
                    }))}
                    min={1}
                    max={100}
                  />
                </div>
              </div>

              {/* Retry Policy Settings */}
              <div className={styles.settingsCard}>
                <h4>Retry Policy</h4>
                <div className={styles.settingField}>
                  <label>Max Attempts</label>
                  <input
                    type="number"
                    value={workflowConfig.retryPolicy.maxAttempts}
                    onChange={(e) => setWorkflowConfig(prev => ({
                      ...prev,
                      retryPolicy: {
                        ...prev.retryPolicy,
                        maxAttempts: parseInt(e.target.value) || 9
                      }
                    }))}
                    min={1}
                    max={20}
                  />
                </div>
                <div className={styles.settingField}>
                  <label>Initial Interval (seconds)</label>
                  <input
                    type="number"
                    value={workflowConfig.retryPolicy.initialIntervalSeconds}
                    onChange={(e) => setWorkflowConfig(prev => ({
                      ...prev,
                      retryPolicy: {
                        ...prev.retryPolicy,
                        initialIntervalSeconds: parseInt(e.target.value) || 5
                      }
                    }))}
                    min={1}
                    max={60}
                  />
                </div>
                <div className={styles.settingField}>
                  <label>Max Interval (seconds)</label>
                  <input
                    type="number"
                    value={workflowConfig.retryPolicy.maxIntervalSeconds}
                    onChange={(e) => setWorkflowConfig(prev => ({
                      ...prev,
                      retryPolicy: {
                        ...prev.retryPolicy,
                        maxIntervalSeconds: parseInt(e.target.value) || 50
                      }
                    }))}
                    min={1}
                    max={300}
                  />
                </div>
                <div className={styles.settingField}>
                  <label>Backoff Factor</label>
                  <input
                    type="number"
                    value={workflowConfig.retryPolicy.backoffFactor}
                    onChange={(e) => setWorkflowConfig(prev => ({
                      ...prev,
                      retryPolicy: {
                        ...prev.retryPolicy,
                        backoffFactor: parseInt(e.target.value) || 5
                      }
                    }))}
                    min={1}
                    max={10}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Node Configuration Panel - Only show on canvas tab */}
        {activeTab === 'canvas' && !isConfigCollapsed && selectedNode && (
          <NodeConfigPanel
            node={selectedNode}
            onUpdate={updateNodeData}
            onDelete={deleteNode}
            onDuplicate={duplicateNode}
            onClose={() => {
              setIsConfigCollapsed(true);
              setSelectedNode(null);
            }}
            assistants={assistants}
            tools={[]}
          />
        )}

        {/* Edge Configuration Panel - Only show when edge is selected */}
        {activeTab === 'canvas' && !isConfigCollapsed && selectedEdge && (
          <div className={styles.nodeConfigPanel}>
            <div className={styles.configHeader}>
              <h3>Connection Configuration</h3>
              <button 
                className={styles.iconButton} 
                onClick={() => {
                  setIsConfigCollapsed(true);
                  setSelectedEdge(null);
                }}
              >
                <X size={18} />
              </button>
            </div>
            <div className={styles.configContent}>
              <div className={styles.configSection}>
                <h4>Connection Details</h4>
                <div className={styles.configField}>
                  <label>Source Node</label>
                  <input 
                    type="text" 
                    value={selectedEdge.source} 
                    readOnly 
                    className={styles.readOnlyInput}
                  />
                </div>
                <div className={styles.configField}>
                  <label>Target Node</label>
                  <input 
                    type="text" 
                    value={selectedEdge.target} 
                    readOnly 
                    className={styles.readOnlyInput}
                  />
                </div>
              </div>
              <div className={styles.configSection}>
                <p className={styles.configHint}>
                  Press <kbd>Delete</kbd> or <kbd>Backspace</kbd> to remove this connection, or use the button below.
                </p>
              </div>
            </div>
            <div className={styles.configFooter}>
              <button 
                className={styles.deleteButton}
                onClick={() => deleteEdge(selectedEdge.id)}
              >
                <Trash2 size={16} />
                Delete Connection
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Wrap with ReactFlowProvider
export const WorkflowEditorWithProvider: React.FC<WorkflowEditorProps> = (props) => (
  <ReactFlowProvider>
    <WorkflowEditor {...props} />
  </ReactFlowProvider>
);

export default WorkflowEditorWithProvider;
